import React, { useState } from 'react';
import { Typography, Button, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
// import { ExpandedOwner } from '../../../../../components/styled/ExpandedOwner/ExpandedOwner';
import { EMPTY_MESSAGES } from '../../../../../constants';
import { Owner, OwnershipType, OwnershipTypeEntity } from '../../../../../../../../types.generated';
import { useEntityData, useMutationUrn, useRefetch } from '../../../../../EntityContext';
import { SidebarHeader } from '../../SidebarHeader';
import { EditOwnersModal } from '../EditOwnersModal';
import { ENTITY_PROFILE_OWNERS_ID } from '../../../../../../../onboarding/config/EntityProfileOnboardingConfig';
import { useTranslation } from '../../../../../../../../i18n/useTranslation';
import { OwnershipTypeSection } from './OwnershipTypeSection';
import { getOwnershipTypeName } from '../ownershipUtils';

interface Props {
    properties?: any;
    readOnly?: boolean;
}

export const SidebarOwnerSection = ({ properties, readOnly }: Props) => {
    const { t } = useTranslation();
    const { entityType, entityData } = useEntityData();
    const mutationUrn = useMutationUrn();
    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = readOnly || entityData?.readOnly?.readOnly === true;

    const refetch = useRefetch();
    const [showAddModal, setShowAddModal] = useState(false);
    const ownersEmpty = !entityData?.ownership?.owners?.length;
    const ownershipTypesMap: Map<string, OwnershipTypeEntity> = new Map();
    const ownersByTypeMap: Map<string, Owner[]> = new Map();
    entityData?.ownership?.owners?.forEach((owner) => {
        const ownershipType = owner?.ownershipType;
        const ownershipTypeName = getOwnershipTypeName(ownershipType);
        // If ownership type is not in the map, add it
        if (ownershipType && !ownershipTypesMap.has(ownershipTypeName)) {
            ownershipTypesMap.set(ownershipTypeName, ownershipType);
        }
        if (!ownersByTypeMap.has(ownershipTypeName)) {
            ownersByTypeMap.set(ownershipTypeName, []);
        }
        ownersByTypeMap.get(ownershipTypeName)?.push(owner);
    });
    // Sort ownership types by name alphabetically
    const ownershipTypeNames = Array.from(ownershipTypesMap.keys()).sort();

    let defaultOwnerTypeUrn = 'urn:li:ownershipType:__system__technical_owner';
    switch (properties?.defaultOwnerType) {
        case OwnershipType.TechnicalOwner:
            defaultOwnerTypeUrn = 'urn:li:ownershipType:__system__technical_owner';
            break;
        case OwnershipType.BusinessOwner:
            defaultOwnerTypeUrn = 'urn:li:ownershipType:__system__business_owner';
            break;
        case OwnershipType.DataSteward:
            defaultOwnerTypeUrn = 'urn:li:ownershipType:__system__data_steward';
            break;
        default:
            break;
    }

    return (
        <div id={ENTITY_PROFILE_OWNERS_ID} className="pa-4">
            <SidebarHeader title={t('Owners')} />
            <div>
                {ownershipTypeNames.map((ownershipTypeName) => {
                    const ownershipType = ownershipTypesMap.get(ownershipTypeName) as OwnershipTypeEntity;
                    const owners = ownersByTypeMap.get(ownershipTypeName) as Owner[];
                    return <OwnershipTypeSection ownershipType={ownershipType} owners={owners} readOnly={isReadOnly} />;
                })}
                {ownersEmpty && (
                    <Typography.Paragraph type="secondary" className="f-text-content f-color-dark-black-s70">
                        {t(EMPTY_MESSAGES.owners.title)}. {t(EMPTY_MESSAGES.owners.description)}
                    </Typography.Paragraph>
                )}
                <Tooltip title={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined}>
                    <Button
                        type={ownersEmpty ? 'default' : 'text'}
                        onClick={() => setShowAddModal(true)}
                        data-testid="add-owners-button"
                        aria-label={t('Add Owners')}
                        disabled={isReadOnly}
                    >
                        <PlusOutlined aria-hidden="true" /> {t('Add Owners')}
                    </Button>
                </Tooltip>
            </div>
            {showAddModal && (
                <EditOwnersModal
                    urns={[mutationUrn]}
                    defaultOwnerType={defaultOwnerTypeUrn}
                    hideOwnerType={properties?.hideOwnerType || false}
                    entityType={entityType}
                    refetch={refetch}
                    onCloseModal={() => {
                        setShowAddModal(false);
                    }}
                />
            )}
        </div>
    );
};
